import React from 'react';
import { t, Trans } from '@lingui/macro';
import { FormCard, FormCardSection } from 'components/base/card/card';
import {
  Field,
  FieldArray,
  Form,
  Formik,
  FormikErrors,
  FormikHelpers,
  FormikProps,
} from 'formik';
import { StoredVerticalProfilePlotWithArea } from 'ducks/stored-plot/detail';
import { Model, Enum } from 'util/backendapi/models/api.interfaces';
import ActionBlock from 'components/base/actionblock/actionblock';
import {
  makeStoredPlotNameSectionInitialValues,
  validateStoredPlotNameSection,
  StoredPlotNameSection,
} from './sections/StoredPlotNameSection';
import { StoredPlotSaveCancelButtons } from './StoredPlotSaveCancelButtons';
import { NewStoredPlotDefaults } from './stored-plot-edit-utils';
import {
  StoredPlotSaveAsValues,
  makeStoredPlotSaveAsInitialValues,
  validateStoredPlotSaveAsValues,
  getStoredPlotSaveAsSettings,
  StoredPlotSaveAsSettings,
} from './StoredPlotSaveAsModal';
import { showStoredPlotErrorsInFormik } from './StoredPlotEdit';
import Button from 'components/base/button/button';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from 'react-beautiful-dnd';
import classNames from 'classnames';
import { ObsPointMenu } from 'components/modules/async-menu/ObsPointMenu';
import { FieldError } from 'components/base/form/errornotice/errornotice';
import { NumberField } from 'components/base/form/number-field/NumberField';
import {
  ToggleField,
  YesNoToggleField,
} from 'components/base/form/toggle-field/ToggleField';
import { DatetimeField } from 'components/base/form/datefield/datefield';
import { validateNumber } from 'util/validation';
import { RadioFieldOption } from 'components/base/form/radio-field/RadioField';
import lodashSet from 'lodash/set';
import { SimpleSelectOption } from 'components/base/form/simpleselect/simpleselect';
import { SimpleSelectField } from 'components/base/form/simpleselect/simpleselectfield';
import FormChangeEffect from 'components/base/form/formchangeeffect/formchangeeffect';
import { range } from 'lodash';
import { ButtonShowConfirmation } from 'components/base/confirmation/ButtonShowConfirmation';
import './StoredPlotEdit.scss';
import './StoredVerticalProfilePlotForm.scss';

export type StoredVerticalProfilePlotFormValues = Merge<
  Model.StoredVerticalProfilePlot_POST,
  StoredPlotSaveAsValues
>;

function makeEmptyStoredVerticalProfilePlotItem(): StoredVerticalProfilePlotFormValues['stored_vertical_profile_plot_items'][0] {
  return {
    name: '',
    description: '',
    observation_point_depths: [{ observation_point: 0, depth: '' }],
    x_axis_range_min: '',
    x_axis_range_max: '',
    y_axis_range_min: '',
    y_axis_range_max: '',
  };
}

function makeInitialValues(
  storedPlot: StoredVerticalProfilePlotWithArea | null,
  newPlotDefaults: NewStoredPlotDefaults | null
): StoredVerticalProfilePlotFormValues {
  if (storedPlot) {
    return {
      ...makeStoredPlotNameSectionInitialValues(storedPlot),
      ...makeStoredPlotSaveAsInitialValues(),
      plot_type: Enum.PlotType.VERTICAL_PROFILE,
      plots_per_page: storedPlot.plots_per_page,
      show_latest_reading: storedPlot.show_latest_reading,
      show_latest_reading_markers: storedPlot.show_latest_reading_markers,
      reading_times: storedPlot.reading_times,
      stored_vertical_profile_plot_items:
        storedPlot.stored_vertical_profile_plot_items.map((item) => ({
          name: item.name,
          description: item.description,
          observation_point_depths: item.observation_point_depths,
          x_axis_range_min: item.x_axis_range_min ?? '',
          x_axis_range_max: item.x_axis_range_max ?? '',
          y_axis_range_min: item.y_axis_range_min ?? '',
          y_axis_range_max: item.y_axis_range_max ?? '',
        })),
    };
  } else {
    return {
      ...makeStoredPlotSaveAsInitialValues(),
      plot_type: Enum.PlotType.VERTICAL_PROFILE,
      name: newPlotDefaults?.name ?? '',
      plots_per_page: 1,
      show_latest_reading: false,
      show_latest_reading_markers: false,
      reading_times: [{ reading_datetime: '', show_markers: false }],
      area: 0,
      stored_vertical_profile_plot_items: [
        makeEmptyStoredVerticalProfilePlotItem(),
      ],
    };
  }
}

function validateAxisRange(
  values: StoredVerticalProfilePlotFormValues['stored_vertical_profile_plot_items'][0],
  errors: FormikErrors<
    StoredVerticalProfilePlotFormValues['stored_vertical_profile_plot_items'][0]
  >,
  min_field: 'x_axis_range_min' | 'y_axis_range_min',
  max_field: 'x_axis_range_max' | 'y_axis_range_max'
) {
  const min_value = values[min_field];
  const max_value = values[max_field];

  const isMinSupplied = Boolean(min_value);
  const isMinValid = isMinSupplied && validateNumber(min_value);
  const isMaxSupplied = Boolean(max_value);
  const isMaxValid = isMaxSupplied && validateNumber(max_value);

  if (isMinValid && isMaxValid && +min_value! >= +max_value!) {
    errors[min_field] = (<Trans>Min must be less than Max</Trans>) as any;
  } else {
    if ((isMinSupplied && !isMinValid) || (isMaxSupplied && !isMinSupplied)) {
      errors[min_field] = (
        <Trans>
          Minimum and maximum must either be both a number or both empty.
        </Trans>
      ) as any;
    }
    if ((isMaxSupplied && !isMaxValid) || (isMinSupplied && !isMaxSupplied)) {
      errors[max_field] = (
        <Trans>
          Minimum and maximum must either be both a number or both empty.
        </Trans>
      ) as any;
    }
  }
}

function validateForm(
  values: StoredVerticalProfilePlotFormValues
): FormikErrors<StoredVerticalProfilePlotFormValues> {
  let errors: FormikErrors<StoredVerticalProfilePlotFormValues> = {
    ...validateStoredPlotNameSection(values),
    ...validateStoredPlotSaveAsValues(values),
  };

  values.reading_times.forEach((readingTime, idx) => {
    if (!readingTime.reading_datetime) {
      lodashSet(
        errors,
        ['reading_times', idx, 'reading_datetime'],
        <Trans>Time period is required.</Trans>
      );
    }
  });

  values.stored_vertical_profile_plot_items.forEach((item, itemIndex) => {
    let itemErrors = {};
    validateAxisRange(item, itemErrors, 'x_axis_range_min', 'x_axis_range_max');
    validateAxisRange(item, itemErrors, 'y_axis_range_min', 'y_axis_range_max');

    Object.keys(itemErrors).forEach((key) => {
      lodashSet(
        errors,
        ['stored_vertical_profile_plot_items', itemIndex, key],
        itemErrors[key]
      );
    });

    item.observation_point_depths.forEach((obsPointDepth, obsPointIndex) => {
      if (!obsPointDepth.observation_point) {
        lodashSet(
          errors,
          [
            'stored_vertical_profile_plot_items',
            itemIndex,
            'observation_point_depths',
            obsPointIndex,
            'observation_point',
          ],
          <Trans>Observation point is required.</Trans>
        );
      }
      if (obsPointDepth.depth === '') {
        lodashSet(
          errors,
          [
            'stored_vertical_profile_plot_items',
            itemIndex,
            'observation_point_depths',
            obsPointIndex,
            'depth',
          ],
          <Trans>Depth is required.</Trans>
        );
      }
    });
  });

  return errors;
}

export interface StoredVerticalProfilePlotFormProps {
  storedPlot: StoredVerticalProfilePlotWithArea | null;
  newPlotDefaults: NewStoredPlotDefaults | null;
  areaOptions: { value: number; label: string; timeZone: string }[];
  onSubmit: (
    values: Model.StoredVerticalProfilePlot_POST,
    saveAs: StoredPlotSaveAsSettings | null
  ) => Promise<any>;
  onCancel: () => void;
}

function ShowMarkersToggleField(props: { name: string }) {
  const showMarkerOptions: RadioFieldOption<boolean>[] = [
    { label: <Trans>Show markers</Trans>, value: true },
    { label: <Trans>Hide markers</Trans>, value: false },
  ];
  return <ToggleField {...props} options={showMarkerOptions} />;
}

function ReadingTimesSection(props: {
  formik: FormikProps<StoredVerticalProfilePlotFormValues>;
  timeZone: string | undefined;
}) {
  const { formik, timeZone } = props;

  return (
    <>
      <FieldArray name="reading_times">
        {(readingTimesArrayHelpers) => (
          <FormCardSection
            name="reading-times"
            header={<Trans>Reading times</Trans>}
            fields={[
              {
                name: 'sub-heading',
                className: 'reading-times-sub-heading',
                content: (
                  <Trans>
                    A data series will be plotted for each specified time. The
                    system will select and plot the nearest reading for each
                    observation point at the specified time.
                  </Trans>
                ),
              },
              {
                label: <Trans>Show latest reading</Trans>,
                name: 'show-latest-reading',
                content: (
                  <table className="reading-times-field-table">
                    <tbody>
                      <tr>
                        <td>
                          <YesNoToggleField name="show_latest_reading" />
                        </td>
                        <td>
                          <ShowMarkersToggleField name="show_latest_reading_markers" />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                ),
              },
              ...formik.values.reading_times.map(
                (readingTime, readingTimeIdx) => {
                  const readingTimeField = `reading_times.[${readingTimeIdx}].reading_datetime`;
                  const showMarkersField = `reading_times.[${readingTimeIdx}].show_markers`;
                  return {
                    label: (
                      <>
                        <Trans>Reading time</Trans> {readingTimeIdx + 1}
                      </>
                    ),
                    className: 'reading-time-field',
                    name: `reading-time-${readingTimeIdx}`,
                    content: (
                      <table className="reading-times-field-table">
                        <tbody>
                          <tr>
                            <td>
                              <DatetimeField
                                name={readingTimeField}
                                timeZone={timeZone}
                              />
                              <FieldError name={readingTimeField} />
                            </td>
                            <td>
                              <ShowMarkersToggleField name={showMarkersField} />
                            </td>
                            <td>
                              {formik.values.reading_times.length > 1 && (
                                <Button
                                  className="reading-time-delete-button"
                                  onClick={() => {
                                    readingTimesArrayHelpers.remove(
                                      readingTimeIdx
                                    );
                                  }}
                                >
                                  <Trans>Delete</Trans>
                                </Button>
                              )}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    ),
                  };
                }
              ),
              {
                name: 'add-reading-time',
                content: (
                  <Button
                    className="add-reading-time-button"
                    iconType="icon-plus"
                    onClick={() => {
                      readingTimesArrayHelpers.push({
                        reading_datetime: '',
                        show_markers: false,
                      });
                    }}
                  >
                    <Trans>Add time period</Trans>
                  </Button>
                ),
              },
            ]}
          />
        )}
      </FieldArray>
    </>
  );
}

function ObservationPointDepthsSection(props: {
  formik: FormikProps<StoredVerticalProfilePlotFormValues>;
  itemIndex: number;
}) {
  const { formik, itemIndex } = props;

  const obsPointDepthsFieldName = `stored_vertical_profile_plot_items.${itemIndex}.observation_point_depths`;

  const obsPointDepthsValues =
    formik.values.stored_vertical_profile_plot_items[itemIndex]
      .observation_point_depths;

  return (
    <>
      <FieldArray name={obsPointDepthsFieldName}>
        {(obsPointDepthsArrayHelpers) => (
          <FormCardSection
            name="obs-point-depths"
            header={
              <>
                <Trans>Observation points</Trans>
              </>
            }
            fields={[
              {
                name: 'add-obs-point-button',
                content: (
                  <Button
                    className="add-obs-point-depth-button"
                    iconType="icon-plus"
                    onClick={() => {
                      obsPointDepthsArrayHelpers.push({
                        observation_point: 0,
                        depth: '',
                      });
                    }}
                  >
                    <Trans>Add observation point</Trans>
                  </Button>
                ),
              },
              {
                name: 'obs-points-table',
                className: 'table-responsive drag-drop-table-with-inputs',
                content: (
                  <DragDropContext
                    onDragEnd={(result: DropResult) => {
                      if (
                        result.destination &&
                        result.source.index !== result.destination.index
                      ) {
                        obsPointDepthsArrayHelpers.move(
                          result.source.index,
                          result.destination.index
                        );
                      }
                    }}
                  >
                    <table className="stored-plot-series-table">
                      <thead>
                        <tr>
                          <th scope="col">
                            <Trans>Observation point</Trans>
                          </th>
                          <th scope="col">
                            <Trans>Depth</Trans>
                          </th>
                          <th scope="col" className="action-icons">
                            <Trans>Actions</Trans>
                          </th>
                        </tr>
                      </thead>
                      <Droppable
                        droppableId={`obs-point-depths-droppable-${itemIndex}`}
                      >
                        {(provided) => (
                          <tbody
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                          >
                            {obsPointDepthsValues.map(
                              (obsPoint, obsPointIdx) => {
                                const obsPointFieldPrefix = `${obsPointDepthsFieldName}.[${obsPointIdx}]`;
                                const draggableId = `obs-point-depths-draggable-${itemIndex}-${obsPointIdx}`;
                                return (
                                  <Draggable
                                    key={draggableId}
                                    draggableId={draggableId}
                                    index={obsPointIdx}
                                    isDragDisabled={
                                      obsPointDepthsValues.length === 1
                                    }
                                  >
                                    {(provided, snapshot) => (
                                      <tr
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        ref={provided.innerRef}
                                        className={classNames({
                                          draggable:
                                            obsPointDepthsValues.length > 1,
                                          dragging: snapshot.isDragging,
                                        })}
                                      >
                                        <td>
                                          <ObsPointMenu
                                            name={`${obsPointFieldPrefix}.observation_point`}
                                            isMulti={false}
                                            className="vertical-profile-obs-point-menu"
                                          />
                                          <FieldError
                                            name={`${obsPointFieldPrefix}.observation_point`}
                                          />
                                        </td>
                                        <td>
                                          <NumberField
                                            name={`${obsPointFieldPrefix}.depth`}
                                          />
                                          <FieldError
                                            name={`${obsPointFieldPrefix}.depth`}
                                          />
                                        </td>
                                        <td className="action-icons">
                                          {obsPointDepthsValues.length > 1 && (
                                            <Button
                                              iconOnly={true}
                                              iconType="icon-circle-minus"
                                              className="btn-link-panel"
                                              title={t`Delete`}
                                              name={`${obsPointFieldPrefix}-delete`}
                                              onClick={() => {
                                                obsPointDepthsArrayHelpers.remove(
                                                  obsPointIdx
                                                );
                                              }}
                                            />
                                          )}
                                        </td>
                                      </tr>
                                    )}
                                  </Draggable>
                                );
                              }
                            )}
                            {provided.placeholder}
                          </tbody>
                        )}
                      </Droppable>
                    </table>
                  </DragDropContext>
                ),
              },
            ]}
          />
        )}
      </FieldArray>
    </>
  );
}

function AxisRangeSection(props: { name: string; label: React.ReactNode }) {
  const { label, name } = props;

  const minFieldName = `${name}_min`;
  const maxFieldName = `${name}_max`;

  return (
    <FormCardSection
      name={name}
      header={label}
      fields={[
        {
          name: name,
          columns: [
            {
              name: 'axis-range-min',
              label: <Trans>Min</Trans>,
              content: (
                <>
                  <NumberField name={minFieldName} required={false} />
                  <FieldError name={minFieldName} />
                </>
              ),
            },
            {
              name: 'axis-range-max',
              label: <Trans>Max</Trans>,
              content: (
                <>
                  <NumberField name={maxFieldName} required={false} />
                  <FieldError name={maxFieldName} />
                </>
              ),
            },
          ],
        },
      ]}
    />
  );
}

interface DeleteStoredVerticalProfilePlotItemButtonProps {
  formik: FormikProps<StoredVerticalProfilePlotFormValues>;
  itemIndex: number;
  itemFieldPrefix: string;
}

function DeleteStoredVerticalProfilePlotItemButton(
  props: DeleteStoredVerticalProfilePlotItemButtonProps
) {
  const { itemFieldPrefix, itemIndex, formik } = props;
  return (
    <ActionBlock>
      <ButtonShowConfirmation
        name={`plot-item-${itemIndex + 1}-delete`}
        onConfirm={async () => {
          formik.setFieldValue(
            itemFieldPrefix,
            makeEmptyStoredVerticalProfilePlotItem()
          );
        }}
        content={
          <Trans>
            Are you sure you want to delete{' '}
            <strong>Plot {itemIndex + 1}</strong> from this stored plot? This
            action is not reversible.
          </Trans>
        }
        okBtnText={<Trans>Yes, delete</Trans>}
      >
        <Trans>Delete</Trans>
      </ButtonShowConfirmation>
    </ActionBlock>
  );
}

interface StoredVerticalProfilePlotItemSectionProps {
  formik: FormikProps<StoredVerticalProfilePlotFormValues>;
  itemIndex: number;
}

function StoredVerticalProfilePlotItemSection(
  props: StoredVerticalProfilePlotItemSectionProps
) {
  const { formik, itemIndex } = props;

  // All the fields for this item will be prefixed with this
  const itemFieldPrefix = `stored_vertical_profile_plot_items[${itemIndex}]`;

  return (
    <FormCard
      name={`vertical-profile-plot-item-${itemIndex + 1}`}
      header={<Trans>Plot {itemIndex + 1}</Trans>}
      subHeader={
        <DeleteStoredVerticalProfilePlotItemButton
          formik={formik}
          itemFieldPrefix={itemFieldPrefix}
          itemIndex={itemIndex}
        />
      }
      className="stored-plot-plot-card"
      data-testid="stored-plot-item"
    >
      <FormCardSection
        name={`vertical-profile-plot-item-${itemIndex + 1}-name`}
        header={<Trans>Name</Trans>}
        fields={[
          {
            name: `${itemFieldPrefix}.name`,
            label: <Trans>Title</Trans>,
            content: (
              <>
                <Field type="text" name={`${itemFieldPrefix}.name`} />
                <FieldError name={`${itemFieldPrefix}.name`} />
              </>
            ),
          },
          {
            name: `${itemFieldPrefix}.description`,
            label: <Trans>Description</Trans>,
            content: (
              <>
                <Field
                  type="text"
                  name={`${itemFieldPrefix}.description`}
                  component="textarea"
                />
                <FieldError name={`${itemFieldPrefix}.description`} />
              </>
            ),
          },
        ]}
      />
      <AxisRangeSection
        name={`${itemFieldPrefix}.x_axis_range`}
        label={<Trans>X-Axis range</Trans>}
      />
      <AxisRangeSection
        name={`${itemFieldPrefix}.y_axis_range`}
        label={<Trans>Y-Axis range</Trans>}
      />
      <ObservationPointDepthsSection formik={formik} itemIndex={itemIndex} />
    </FormCard>
  );
}

function StoredVerticalProfilePlotLayoutSection(props: {
  formik: FormikProps<StoredVerticalProfilePlotFormValues>;
}) {
  const { formik } = props;

  let maxPlotsPerPage = 2;
  let plotsPerPageOptions: SimpleSelectOption<number>[] = [];

  if (maxPlotsPerPage <= 1) return null;

  for (let i = 1; i <= maxPlotsPerPage; i++) {
    plotsPerPageOptions.push({ label: String(i), value: i });
  }

  const layoutFields = [
    {
      name: 'plots_per_page',
      label: <Trans>Plots per page</Trans>,
      content: (
        <>
          <SimpleSelectField
            name="plots_per_page"
            options={plotsPerPageOptions}
          />
          <FieldError name="plots_per_page" />
        </>
      ),
    },
  ];

  return (
    <>
      <FormCardSection
        name="layout"
        header={<Trans>Layout</Trans>}
        fields={layoutFields}
      />

      {/* effect to update the layout dropdown when number of plots changed */}
      {/* aslo add/remove the plot items based on `plots_per_page`  */}
      <FormChangeEffect<StoredVerticalProfilePlotFormValues>
        onChange={({ values: prevValues }) => {
          if (prevValues.plots_per_page !== formik.values.plots_per_page) {
            const itemCounts =
              formik.values.stored_vertical_profile_plot_items.length;

            if (formik.values.plots_per_page > itemCounts) {
              const addThisMany = formik.values.plots_per_page - itemCounts;
              formik.setFieldValue(
                'stored_vertical_profile_plot_items',
                formik.values.stored_vertical_profile_plot_items.concat(
                  range(addThisMany).map(() =>
                    makeEmptyStoredVerticalProfilePlotItem()
                  )
                )
              );
            } else if (formik.values.plots_per_page < itemCounts) {
              formik.setFieldValue(
                'stored_vertical_profile_plot_items',
                formik.values.stored_vertical_profile_plot_items.slice(
                  0,
                  formik.values.plots_per_page
                )
              );
            }
          }
        }}
      />
    </>
  );
}

export const StoredVerticalProfilePlotForm = (
  props: StoredVerticalProfilePlotFormProps
) => {
  const { areaOptions, storedPlot, newPlotDefaults } = props;

  const handleSubmit = React.useCallback(
    async function (
      values: StoredVerticalProfilePlotFormValues,
      formik: FormikHelpers<StoredVerticalProfilePlotFormValues>
    ) {
      const valuesForBackend: Model.StoredVerticalProfilePlot_POST = {
        ...values,
      };
      try {
        await props.onSubmit.call(
          null,
          valuesForBackend,
          getStoredPlotSaveAsSettings(values)
        );
      } catch (e) {
        formik.setSubmitting(false);
        showStoredPlotErrorsInFormik(formik, e, values);
      }
    },
    [props.onSubmit]
  );

  return (
    <Formik<StoredVerticalProfilePlotFormValues>
      validate={validateForm}
      initialValues={makeInitialValues(storedPlot, newPlotDefaults)}
      onSubmit={handleSubmit}
    >
      {(formik) => {
        const timeZone = formik.values.area
          ? areaOptions.find((a) => a.value === formik.values.area)?.timeZone
          : undefined;
        return (
          <Form>
            {formik.status}
            <FormCard
              name={'general'}
              header={<Trans>General</Trans>}
              className="stored-plot-general-card"
              subHeader={
                <StoredPlotSaveCancelButtons {...props} formik={formik} />
              }
            >
              <StoredPlotNameSection
                areaOptions={areaOptions}
                plotType={Enum.PlotType.VERTICAL_PROFILE}
              />
              <ReadingTimesSection formik={formik} timeZone={timeZone} />
              <StoredVerticalProfilePlotLayoutSection formik={formik} />
            </FormCard>

            {formik.values.stored_vertical_profile_plot_items.map(
              (_item, itemIndex) => (
                <StoredVerticalProfilePlotItemSection
                  key={itemIndex}
                  formik={formik}
                  itemIndex={itemIndex}
                />
              )
            )}

            <ActionBlock className="text-right">
              <StoredPlotSaveCancelButtons {...props} formik={formik} />
            </ActionBlock>
          </Form>
        );
      }}
    </Formik>
  );
};
